import React, { useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupsIcon from '@mui/icons-material/Groups';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import type { GameNameType } from 'src/games/types';
import {
  SETTINGS_FOR_GAMES,
  GAMES_DESCRIPTION,
  GAMES_ESTIMATED_DURATION,
} from '../settingsForGames';

import styles from './GameSquare.module.scss';

import rummikub from './images/rummikub.jpg';
import abcd from './images/abcd.png';
import tictactoe from './images/tictactoe.jpg';
import retrosnake from './images/retrosnake.png';
import spotmatch from './images/spotmatch.jpg';
import speedmaze from './images/speedmaze.png';
import NumberSpinner from 'src/components/common/NumberSpinner';
import { GameType } from './types';

const images: Partial<Record<GameNameType, string>> = {
  rummikub,
  abcd,
  tictactoe,
  retrosnake,
  spotmatch,
  speedmaze,
};

const GameSquareInListActions = ({
  game,
  addGame,
}: GameSquareInListPropsType) => {
  const [moreInfoOpened, setMoreInfoOpened] = useState(false);

  return (
    <>
      <Dialog open={moreInfoOpened} onClose={() => setMoreInfoOpened(false)}>
        <DialogTitle>{game.name}</DialogTitle>
        <DialogContent>{GAMES_DESCRIPTION[game.name]}</DialogContent>
      </Dialog>
      <Button
        variant="contained"
        style={{ minWidth: 0, width: '32px', height: '32px' }}
        color="info"
        onClick={() => setMoreInfoOpened(true)}
      >
        <InfoOutlineIcon fontSize="small" />
      </Button>
      <Button
        variant="contained"
        style={{ minWidth: 0, width: '32px', height: '32px' }}
        onClick={() => addGame(game.name)}
      >
        <AddIcon fontSize="small" />
      </Button>
    </>
  );
};

const GameSquareSelectedActions = (props: GameSquareSelectedPropsType) => {
  const [settingsOpened, setSettingsOpened] = useState(false);

  const settings = SETTINGS_FOR_GAMES[props.game.name].filter(
    (setting) => setting.name !== 'playersNeeded',
  );

  return (
    <>
      <Dialog open={settingsOpened}>
        <DialogTitle>{`${props.game.name} settings`}</DialogTitle>
        <DialogContent>
          <form
            onSubmit={() => setSettingsOpened(false)}
            style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
          >
            {settings.map((setting) => {
              const settingValue =
                props.game.settings && props.game.settings[setting.name];

              return (
                <NumberSpinner
                  key={setting.name}
                  label={setting.display}
                  placeholder={setting.default}
                  value={settingValue ? Number(settingValue) : undefined}
                  onUpdate={(newValue) =>
                    props.updateGameSetting(
                      props.gameNr,
                      setting.name,
                      newValue ? String(newValue) : undefined,
                    )
                  }
                  min={setting.min}
                  max={setting.max}
                />
              );
            })}
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setSettingsOpened(false)}
            >
              Save & close
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      {props.disabledMoveLeft === false && (
        <Button
          variant="contained"
          style={{ minWidth: 0, width: '32px', height: '32px' }}
          color="info"
          onClick={() => props.moveAction(props.gameNr, 'left')}
        >
          <KeyboardArrowLeftIcon fontSize="small" />
        </Button>
      )}
      {props.disabledMoveRight === false && (
        <Button
          variant="contained"
          style={{ minWidth: 0, width: '32px', height: '32px' }}
          color="info"
          onClick={() => props.moveAction(props.gameNr, 'right')}
        >
          <KeyboardArrowRightIcon fontSize="small" />
        </Button>
      )}
      <Button
        variant="contained"
        style={{ minWidth: 0, width: '32px', height: '32px' }}
        color="info"
        onClick={() => setSettingsOpened(true)}
      >
        <SettingsIcon fontSize="small" />
      </Button>
      <Button
        variant="contained"
        style={{ minWidth: 0, width: '32px', height: '32px' }}
        onClick={() => props.removeGame(props.gameNr)}
      >
        <CloseIcon fontSize="small" />
      </Button>
    </>
  );
};

const GameSquareCheckListActions = ({
  game,
  toggleGame,
  gameEnabled,
}: GameSquareCheckListPropsType) => {
  const [moreInfoOpened, setMoreInfoOpened] = useState(false);

  return (
    <>
      <Dialog open={moreInfoOpened} onClose={() => setMoreInfoOpened(false)}>
        <DialogTitle>{game.name}</DialogTitle>
        <DialogContent>{GAMES_DESCRIPTION[game.name]}</DialogContent>
      </Dialog>
      <Button
        variant="contained"
        style={{ minWidth: 0, width: '32px', height: '32px' }}
        color="info"
        onClick={() => setMoreInfoOpened(true)}
      >
        <InfoOutlineIcon fontSize="small" />
      </Button>
      <Button
        variant="contained"
        style={{ minWidth: 0, width: '32px', height: '32px' }}
        onClick={() => toggleGame()}
      >
        {gameEnabled ? (
          <CloseIcon fontSize="small" />
        ) : (
          <AddIcon fontSize="small" />
        )}
      </Button>
    </>
  );
};

interface GameSquareBasePropsType {
  game: GameType;
  size?: 'medium' | 'large'; // TODO: Add support for smaller GameSquare
  actions?: React.ReactNode;
}

interface GameSquareSelectedPropsType extends GameSquareBasePropsType {
  type: 'selected';
  gameNr: number;
  removeGame: (gameNr: number) => void;
  updateGameSetting: (
    gameNr: number,
    settingName: string,
    value: string | undefined,
  ) => void;
  moveAction: (gameNr: number, where: 'left' | 'right') => void;
  disabledMoveLeft: boolean;
  disabledMoveRight: boolean;
}

interface GameSquareInListPropsType extends GameSquareBasePropsType {
  type: 'in-list';
  addGame: (name: GameNameType) => void;
}

interface GameSquareShowcasePropsType extends GameSquareBasePropsType {
  type: 'showcase';
}

interface GameSquareCheckListPropsType extends GameSquareBasePropsType {
  type: 'check-list';
  toggleGame: () => void;
  gameEnabled: boolean;
}

type GameSquarePropsType =
  | GameSquareSelectedPropsType
  | GameSquareInListPropsType
  | GameSquareShowcasePropsType
  | GameSquareCheckListPropsType;

const GameSquare = (props: GameSquarePropsType) => {
  const playersNeededSetting = SETTINGS_FOR_GAMES[props.game.name].find(
    (setting) => setting.name === 'playersNeeded',
  );

  return (
    <div className={styles.GameSquare}>
      <div className={styles.background}>
        <img src={images[props.game.name]} loading="lazy" />
      </div>

      <div className={styles['top-left-section']}>
        <div title="Estimated duration">
          <AccessTimeIcon sx={{ fontSize: 14 }} />
          {GAMES_ESTIMATED_DURATION[props.game.name]}
        </div>
        <div title="Number of players">
          <GroupsIcon sx={{ fontSize: 14 }} />
          {playersNeededSetting?.min}-{playersNeededSetting?.max}
        </div>
      </div>

      <div className={styles['bottom-section']}>
        <div className={styles['game-name']}>{props.game.name}</div>
        <div className={styles['actions']}>
          {props.actions}
          {props.type === 'in-list' && <GameSquareInListActions {...props} />}
          {props.type === 'selected' && (
            <GameSquareSelectedActions {...props} />
          )}
          {props.type === 'check-list' && (
            <GameSquareCheckListActions {...props} />
          )}
        </div>
      </div>
    </div>
  );
};

export default GameSquare;
